import { useState } from 'react'
import type { PublicTransfer } from '../types'
import { formatFileSize } from '../crypto'
import { FileIcon } from './FileIcon'

interface PasswordFormProps {
  transfer: PublicTransfer
  onSubmit: (password: string) => void
}

export function PasswordForm({ transfer, onSubmit }: PasswordFormProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!password.trim()) {
      setError('Please enter the password')
      return
    }

    setError('')
    setIsSubmitting(true)
    
    try {
      await onSubmit(password)
    } catch {
      setError('Invalid password')
      setIsSubmitting(false)
    }
  }

  const file = transfer.files[0]

  return (
    <div>
      {/* File info */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-md)',
        marginBottom: 'var(--space-lg)',
        padding: 'var(--space-md)',
        background: 'var(--bg)',
        borderRadius: 'var(--radius-md)',
      }}>
        <FileIcon filename={file?.name || ''} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {file?.name || 'Encrypted file'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {formatFileSize(transfer.total_size)}
          </div>
        </div>
      </div>

      {/* Lock icon */}
      <div style={{ textAlign: 'center', marginBottom: 'var(--space-lg)' }}>
        <div style={{ 
          width: '56px',
          height: '56px',
          borderRadius: 'var(--radius-full)',
          background: 'rgba(255, 214, 10, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="11" width="18" height="11" rx="2" stroke="var(--warning)" strokeWidth="2" />
            <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h2 style={{ 
          marginTop: 'var(--space-md)', 
          fontSize: '18px', 
          fontWeight: 600,
        }}>
          Password Protected
        </h2>
        <p style={{ 
          color: 'var(--text-muted)', 
          fontSize: '14px',
          marginTop: 'var(--space-xs)',
        }}>
          Enter the password to download this file
        </p>
      </div>

      {/* Password form */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 'var(--space-md)' }}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoFocus
            disabled={isSubmitting}
            style={{
              borderColor: error ? 'var(--error)' : undefined,
            }}
          />
          {error && (
            <p style={{ 
              color: 'var(--error)', 
              fontSize: '13px',
              marginTop: 'var(--space-xs)',
            }}>
              {error}
            </p>
          )}
        </div>

        <button 
          type="submit" 
          className="btn btn-primary btn-large"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                className="animate-spin"
                fill="none"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
                <path d="M8 2C11.3137 2 14 4.68629 14 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Verifying...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M12 3V15M12 15L7 10M12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Unlock & Download
            </>
          )}
        </button>
      </form>
    </div>
  )
}
